import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatDateLabel,
    formatNumberItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    SortableHeader,
    TablePagination,
    TableSearch,
    filterRowsBySearch,
    useSortableRows,
} from "./ReportingLab.jsx";

export const AdvertisingReportView = Object.freeze({
    Formats: "formats",
    CampaignMix: "campaign-mix",
});

const CategoryColors = {
    website: "#2563eb",
    "product-catalog": "#f59e0b",
    "lead-form": "#16a34a",
    messaging: "#06b6d4",
    "phone-call": "#f97316",
    app: "#db2777",
    "store-visit": "#ca8a04",
    "instant-experience": "#9333ea",
    engagement: "#0891b2",
    video: "#dc2626",
    "on-platform": "#db2777",
    unknown: "#64748b",
    "not-classified": "#cbd5e1",
    mixed: "#475569",
};

export function categoryLabel(t, value) {
    switch (value) {
        case "website": return t("reporting_lab.advertising_classification.category_website", {defaultValue: "Webová reklama"});
        case "product-catalog": return t("reporting_lab.advertising_classification.category_product_catalog", {defaultValue: "Produktový katalog"});
        case "lead-form": return t("reporting_lab.advertising_classification.category_lead_form", {defaultValue: "Kontaktní formulář"});
        case "messaging": return t("reporting_lab.advertising_classification.category_messaging", {defaultValue: "Zprávy a chat"});
        case "phone-call": return t("reporting_lab.advertising_classification.category_phone_call", {defaultValue: "Telefonní reklama"});
        case "app": return t("reporting_lab.advertising_classification.category_app", {defaultValue: "Aplikace"});
        case "store-visit": return t("reporting_lab.advertising_classification.category_store_visit", {defaultValue: "Návštěva provozovny"});
        case "instant-experience": return t("reporting_lab.advertising_classification.category_instant_experience", {defaultValue: "Instant Experience"});
        case "engagement": return t("reporting_lab.advertising_classification.category_engagement", {defaultValue: "Interakce"});
        case "video": return t("reporting_lab.advertising_classification.category_video", {defaultValue: "Video reklama"});
        case "on-platform": return t("reporting_lab.advertising_classification.category_on_platform", {defaultValue: "Reklama na platformě"});
        case "mixed": return t("reporting_lab.advertising_classification.category_mixed", {defaultValue: "Smíšená kampaň"});
        case "unknown": return t("reporting_lab.advertising_classification.category_unknown", {defaultValue: "Neurčený typ"});
        case "not-classified": return t("reporting_lab.advertising_classification.not_classified", {defaultValue: "Ještě nerozpoznáno"});
        default: return value;
    }
}

export function destinationStatusLabel(t, value) {
    switch (value) {
        case "web-destination": return t("reporting_lab.advertising_classification.destination_web", {defaultValue: "Cíl na webu"});
        case "platform-destination": return t("reporting_lab.advertising_classification.destination_platform", {defaultValue: "Cíl na platformě"});
        case "no-web-destination": return t("reporting_lab.advertising_classification.destination_none", {defaultValue: "Bez návštěvy webu"});
        case "merchant-required": return t("reporting_lab.advertising_classification.destination_catalog_unknown", {defaultValue: "Katalogový cíl není dostupný"});
        case "provider-data-missing": return t("reporting_lab.advertising_classification.destination_provider_missing", {defaultValue: "Poskytovatel cíl nevrátil"});
        case "ambiguous": return t("reporting_lab.advertising_classification.destination_ambiguous", {defaultValue: "Více možných webových cílů"});
        case "not-classified": return t("reporting_lab.advertising_classification.destination_not_classified", {defaultValue: "Ještě nerozpoznáno"});
        default: return t("reporting_lab.advertising_classification.destination_unknown", {defaultValue: "Cíl zatím neznámý"});
    }
}

export function platformLabel(value) {
    return ({
        "google-ads": "Google Ads",
        "meta-ads": "Meta Ads",
        "microsoft-ads": "Microsoft Ads",
        sklik: "Sklik",
        pinterest: "Pinterest Ads",
        glami: "GLAMI",
        heureka: "Heureka",
        zbozi: "Zboží.cz",
        other: "Ostatní",
    })[value] ?? value;
}

export function domainLabel(t, value) {
    return value && value !== "unassigned"
        ? value
        : t("reporting_lab.advertising_classification.domain_unassigned", {defaultValue: "Bez přiřazené domény"});
}

export function percentage(numerator, denominator) {
    return denominator > 0 ? (numerator / denominator) * 100 : null;
}

export function sumMoney(rows) {
    return rows.reduce((sum, row) => sum + moneyAmount(row?.spend), 0);
}

export function advertisingReportSummary(data) {
    const destinations = data?.destinations ?? [];
    const campaignMix = data?.campaignMix ?? [];
    const totalSpend = moneyAmount(data?.spend);
    const campaignSpend = moneyAmount(data?.campaignSpend);
    const classifiedSpend = moneyAmount(data?.classifiedSpend);
    const webSpend = sumMoney(destinations.filter((row) => row.type === "web-destination"));
    const noWebSpend = sumMoney(destinations.filter((row) => ["platform-destination", "no-web-destination"].includes(row.type)));
    const unknownSpend = Math.max(0, totalSpend - webSpend - noWebSpend);
    const mixedCampaigns = campaignMix.find((row) => row.type === "mixed")?.campaignCount ?? 0;
    const campaignCount = campaignMix.reduce((sum, row) => sum + Number(row.campaignCount ?? 0), 0);

    return {totalSpend, campaignSpend, classifiedSpend, webSpend, noWebSpend, unknownSpend, mixedCampaigns, campaignCount};
}

function useAdvertisingFormatData(project) {
    const {since, until, domainsAllowListed} = useContext(FilterContext);
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const sinceString = since.format("YYYY-MM-DD");
    const untilString = until.format("YYYY-MM-DD");
    const domainFilterKey = (domainsAllowListed ?? []).join("|");
    const erpFilter = useMemo(() => ({
        since: sinceString,
        until: untilString,
        selectedCentreIds: [],
        selectedDomainIds: domainsAllowListed,
    }), [sinceString, untilString, domainFilterKey]);

    useEffect(() => {
        if (!project?.id) return;

        let active = true;
        setLoading(true);
        setData(null);

        new ErpCalculationApi(getAuthorityConfiguration()).advertisingFormats({projectId: project.id, erpFilter})
            .then((nextData) => {
                if (active) {
                    setData(nextData);
                    setLoading(false);
                }
            })
            .catch((error) => {
                if (active) {
                    et(error).then((message) => { if (message) toast.error(message); });
                }
            });

        return () => { active = false; };
    }, [project?.id, erpFilter]);

    return {data, loading};
}

function categoryDonutOption(rows, t, currency, valueKey = "spend") {
    const data = rows.map((row) => ({
        name: categoryLabel(t, row.type),
        value: valueKey === "spend" ? moneyToMajor(row.spend) : Number(row[valueKey] ?? 0),
        itemStyle: {color: CategoryColors[row.type] ?? "#64748b"},
    })).filter((row) => row.value > 0);

    return {
        color: ChannelMixColors,
        tooltip: {
            trigger: "item",
            formatter: valueKey === "spend"
                ? (param) => `${param.marker} ${param.name}<span style="float:right;margin-left:16px;font-weight:600">${formatChartCurrency(param.value, currency)}</span><br/><span style="color:#64748b">${formatChartNumber(param.percent, {maximumFractionDigits: 1})} %</span>`
                : formatNumberItemTooltip,
        },
        legend: {data: data.map((row) => row.name)},
        series: [{type: "pie", radius: ["48%", "72%"], center: ["50%", "48%"], label: {formatter: "{b}\n{d}%"}, data}],
    };
}

export function dailySpendOption(rows, t, currency) {
    const dates = [...new Set(rows.map((row) => formatDateLabel(row.date)))];
    const types = [...new Set(rows.map((row) => row.type))];
    const values = new Map(rows.map((row) => [`${formatDateLabel(row.date)}|${row.type}`, moneyToMajor(row.spend)]));

    return {
        color: types.map((type) => CategoryColors[type] ?? "#64748b"),
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency))},
        legend: {data: types.map((type) => categoryLabel(t, type))},
        xAxis: {type: "category", data: dates},
        yAxis: {type: "value", axisLabel: {formatter: (value) => formatChartCurrency(value, currency)}},
        series: types.map((type) => ({
            name: categoryLabel(t, type),
            type: "bar",
            stack: "spend",
            emphasis: {focus: "series"},
            data: dates.map((date) => values.get(`${date}|${type}`) ?? 0),
        })),
    };
}

export function strategyFlowOption(platformRows, domainRows, t, currency) {
    const nodes = new Map();
    const links = [];
    const addNode = (name, label, color = null) => {
        if (!nodes.has(name)) {
            nodes.set(name, {name, label, ...(color ? {itemStyle: {color}} : {})});
        }
    };

    platformRows.forEach((row) => {
        const platformNode = `platform:${row.dimension}`;
        const categoryNode = `category:${row.category}`;
        const platformName = platformLabel(row.dimension);
        const categoryName = categoryLabel(t, row.category);
        addNode(platformNode, platformName);
        addNode(categoryNode, categoryName, CategoryColors[row.category] ?? "#64748b");
        links.push({source: platformNode, target: categoryNode, sourceLabel: platformName, targetLabel: categoryName, value: moneyToMajor(row.spend)});
    });
    domainRows.forEach((row) => {
        const categoryNode = `category:${row.category}`;
        const domainNode = `domain:${row.dimension}`;
        const categoryName = categoryLabel(t, row.category);
        const domainName = domainLabel(t, row.dimension);
        addNode(categoryNode, categoryName, CategoryColors[row.category] ?? "#64748b");
        addNode(domainNode, domainName);
        links.push({source: categoryNode, target: domainNode, sourceLabel: categoryName, targetLabel: domainName, value: moneyToMajor(row.spend)});
    });

    return {
        tooltip: {
            trigger: "item",
            formatter: (param) => param.dataType === "edge"
                ? `${param.data.sourceLabel} → ${param.data.targetLabel}<br/><strong>${formatChartCurrency(param.value, currency)}</strong>`
                : param.data.label,
        },
        series: [{
            type: "sankey",
            left: 24,
            right: 24,
            top: 20,
            bottom: 20,
            nodeWidth: 18,
            nodeGap: 14,
            emphasis: {focus: "adjacency"},
            lineStyle: {color: "gradient", opacity: 0.35, curveness: 0.5},
            label: {formatter: (param) => param.data.label},
            data: [...nodes.values()],
            links: links.filter((link) => link.value > 0),
        }],
    };
}

export function campaignCategorySpendOption(rows, t, currency) {
    const campaigns = new Map();
    rows.forEach((row) => {
        const current = campaigns.get(row.campaignId) ?? {id: row.campaignId, name: row.campaignName, spend: 0};
        current.spend += moneyToMajor(row.spend);
        campaigns.set(row.campaignId, current);
    });
    const orderedCampaigns = [...campaigns.values()].sort((left, right) => left.spend - right.spend);
    const categories = [...new Set(rows.map((row) => row.category))];
    const values = new Map(rows.map((row) => [`${row.campaignId}|${row.category}`, moneyToMajor(row.spend)]));

    return {
        color: categories.map((category) => CategoryColors[category] ?? "#64748b"),
        tooltip: {trigger: "axis", axisPointer: {type: "shadow"}, formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency))},
        legend: {data: categories.map((category) => categoryLabel(t, category))},
        grid: {containLabel: true, left: 16, right: 24},
        xAxis: {type: "value", axisLabel: {formatter: (value) => formatChartCurrency(value, currency)}},
        yAxis: {
            type: "category",
            data: orderedCampaigns.map((campaign) => campaign.id),
            axisLabel: {formatter: (campaignId) => {
                const name = campaigns.get(campaignId)?.name ?? campaignId;
                return name.length > 30 ? `${name.slice(0, 29)}…` : name;
            }},
        },
        series: categories.map((category) => ({
            name: categoryLabel(t, category),
            type: "bar",
            stack: "spend",
            emphasis: {focus: "series"},
            data: orderedCampaigns.map((campaign) => values.get(`${campaign.id}|${category}`) ?? 0),
        })),
    };
}

function median(values) {
    if (values.length === 0) return null;
    const sorted = [...values].sort((left, right) => left - right);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
}

export function campaignEfficiencyOption(rows, t, currency) {
    const eligibleRows = rows.filter((row) => row.ctr !== null && row.ctr !== undefined && row.cpc);
    const maxSpend = Math.max(...eligibleRows.map((row) => moneyToMajor(row.spend)), 1);
    const medianCtr = median(eligibleRows.map((row) => Number(row.ctr)));
    const medianCpc = median(eligibleRows.map((row) => moneyToMajor(row.cpc)));
    const compositions = [...new Set(eligibleRows.map((row) => row.composition))];

    return {
        color: compositions.map((composition) => CategoryColors[composition] ?? "#64748b"),
        tooltip: {trigger: "item", formatter: (param) => `${param.data.name}<br/>${platformLabel(param.data.platform)}<br/>${t("reporting_lab.advertising_classification.spend", {defaultValue: "Spend"})}: <strong>${formatChartCurrency(param.data.value[2], currency)}</strong><br/>CTR: ${formatChartNumber(param.data.value[0], {maximumFractionDigits: 2})} %<br/>CPC: ${formatChartCurrency(param.data.value[1], currency)}`},
        legend: {data: compositions.map((composition) => categoryLabel(t, composition))},
        grid: {containLabel: true, left: 18, right: 24, bottom: 20},
        xAxis: {type: "value", name: "CTR →", nameLocation: "middle", nameGap: 30, axisLabel: {formatter: (value) => `${formatChartNumber(value, {maximumFractionDigits: 1})} %`}},
        yAxis: {type: "value", name: "CPC ↑", axisLabel: {formatter: (value) => formatChartCurrency(value, currency)}},
        series: compositions.map((composition, index) => ({
            name: categoryLabel(t, composition),
            type: "scatter",
            data: eligibleRows.filter((row) => row.composition === composition).map((row) => {
                const spend = moneyToMajor(row.spend);
                return {
                    name: row.name,
                    platform: row.platform,
                    value: [Number(row.ctr), moneyToMajor(row.cpc), spend],
                    symbolSize: 12 + (Math.sqrt(spend / maxSpend) * 34),
                };
            }),
            ...(index === 0 && medianCtr !== null && medianCpc !== null ? {markLine: {
                silent: true,
                symbol: "none",
                lineStyle: {type: "dashed", color: "#94a3b8"},
                label: {color: "#64748b", fontSize: 11},
                data: [
                    {xAxis: medianCtr, label: {formatter: t("reporting_lab.advertising_classification.median_ctr", {defaultValue: "Medián CTR"})}},
                    {yAxis: medianCpc, label: {formatter: t("reporting_lab.advertising_classification.median_cpc", {defaultValue: "Medián CPC"})}},
                ],
            }} : {}),
        })),
    };
}

export function dailyDomainSpendOption(rows, t, currency) {
    const dates = [...new Set(rows.map((row) => formatDateLabel(row.date)))];
    const domains = [...new Set(rows.map((row) => row.type))];
    const values = new Map(rows.map((row) => [`${formatDateLabel(row.date)}|${row.type}`, moneyToMajor(row.spend)]));

    return {
        color: ChannelMixColors,
        tooltip: {trigger: "axis", formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency))},
        legend: {data: domains.map((domain) => domainLabel(t, domain))},
        xAxis: {type: "category", data: dates},
        yAxis: {type: "value", axisLabel: {formatter: (value) => formatChartCurrency(value, currency)}},
        series: domains.map((domain) => ({
            name: domainLabel(t, domain),
            type: "line",
            stack: "spend",
            areaStyle: {},
            emphasis: {focus: "series"},
            data: dates.map((date) => values.get(`${date}|${domain}`) ?? 0),
        })),
    };
}

export default function ReportingAdvertisingClassificationLab({project, view = AdvertisingReportView.Formats}) {
    const {t} = useTranslation();
    const {data, loading} = useAdvertisingFormatData(project);
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const renderPercentValue = useRenderPercentValue();
    const currency = projectCurrency(project);
    const categories = data?.categories ?? [];
    const domains = data?.domains ?? [];
    const campaignMix = data?.campaignMix ?? [];
    const campaignStrategies = data?.campaignStrategies ?? [];
    const mixedCampaigns = campaignMix.find((row) => row.type === "mixed")?.campaignCount ?? 0;
    const campaignCount = campaignMix.reduce((sum, row) => sum + Number(row.campaignCount ?? 0), 0);
    const campaignAdCount = campaignMix.reduce((sum, row) => sum + Number(row.adCount ?? 0), 0);
    const totalClicks = categories.reduce((sum, row) => sum + Number(row.clicks ?? 0), 0);
    const dominantCategory = categories.reduce((current, row) => moneyAmount(row.spend) > moneyAmount(current?.spend) ? row : current, null);
    const dominantDomain = domains.reduce((current, row) => moneyAmount(row.spend) > moneyAmount(current?.spend) ? row : current, null);
    const dominantCampaign = campaignStrategies[0] ?? null;
    const usedCategoryCount = categories.filter((row) => row.type !== "not-classified" && moneyAmount(row.spend) > 0).length;

    const configuration = {
        [AdvertisingReportView.Formats]: {
            title: t("reporting_lab.pages.advertising_classification", {defaultValue: "Strategie reklam"}),
            descriptionTitle: t("reporting_lab.descriptions.advertising_classification.title", {defaultValue: "Jak je rozdělený váš reklamní rozpočet"}),
            description: t("reporting_lab.descriptions.advertising_classification.text", {defaultValue: "Přehled ukazuje zastoupení reklamních formátů, cílových domén a reklamních systémů. Pomůže porovnat, kam směřuje rozpočet a jak jednotlivé části reklamní strategie přivádějí návštěvnost."}),
        },
        [AdvertisingReportView.CampaignMix]: {
            title: t("reporting_lab.pages.advertising_campaign_mix", {defaultValue: "Strategie kampaní"}),
            descriptionTitle: t("reporting_lab.descriptions.advertising_campaign_mix.title", {defaultValue: "Jaké formáty používají konkrétní kampaně"}),
            description: t("reporting_lab.descriptions.advertising_campaign_mix.text", {defaultValue: "Přehled ukazuje skutečné kampaně, jejich reklamní systémy, cílové domény, zastoupené formáty a výkon. Graf efektivity porovnává CTR a CPC; velikost bodu odpovídá spendu kampaně."}),
        },
    }[view];

    return <WideSecureLayout
        title={configuration.title}
        actions={<OrderDashboardFilters showModeSwitch={false} showCentreFilter={false}/>}
    >
        <div className="space-y-4">
            <ReportingPageDescription title={configuration.descriptionTitle}>{configuration.description}</ReportingPageDescription>

            {view === AdvertisingReportView.Formats && <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.spend", {defaultValue: "Spend v období"})} value={renderCurrencyValue(data?.spend, true)} helper={t("reporting_lab.advertising_classification.spend_help", {defaultValue: "Spend dostupný na úrovni jednotlivých reklam"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.dominant_category", {defaultValue: "Hlavní reklamní formát"})} value={dominantCategory ? categoryLabel(t, dominantCategory.type) : "–"} helper={dominantCategory ? t("reporting_lab.advertising_classification.dominant_share", {defaultValue: "{{share}} rozpočtu", share: renderPercentValue(dominantCategory.spendShare)}) : ""}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.dominant_domain", {defaultValue: "Hlavní cílová doména"})} value={dominantDomain ? domainLabel(t, dominantDomain.domain) : "–"} helper={dominantDomain ? t("reporting_lab.advertising_classification.dominant_share", {defaultValue: "{{share}} rozpočtu", share: renderPercentValue(dominantDomain.spendShare)}) : ""}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.campaigns_in_period", {defaultValue: "Kampaně v období"})} value={renderNumber(campaignCount)} helper={t("reporting_lab.advertising_classification.campaigns_in_period_help", {defaultValue: "Kampaně s aktivitou reklam ve výběru"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.active_ads", {defaultValue: "Reklamy v období"})} value={renderNumber(data?.adCount ?? 0)} helper={t("reporting_lab.advertising_classification.active_ads_help", {defaultValue: "Reklamy s alespoň jedním denním výkonem ve výběru"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.clicks", {defaultValue: "Kliknutí"})} value={renderNumber(totalClicks)} helper={t("reporting_lab.advertising_classification.clicks_help", {defaultValue: "Kliknutí na reklamy ve zvoleném období"})}/>
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Panel loading={loading} title={t("reporting_lab.advertising_classification.spend_mix", {defaultValue: "Rozložení spendu podle typu"})}><EChart option={categoryDonutOption(categories, t, currency)} loading={loading}/></Panel>
                    <Panel loading={loading} title={t("reporting_lab.advertising_classification.spend_trend", {defaultValue: "Vývoj spendu podle typu"})}><EChart option={dailySpendOption(data?.dailyCategories ?? [], t, currency)} loading={loading}/></Panel>
                </div>
                <SummaryTablePanel loading={loading} title={t("reporting_lab.advertising_classification.by_category", {defaultValue: "Výkon podle typu reklamy"})} rows={categories} labelFor={(value) => categoryLabel(t, value)} firstLabel={t("reporting_lab.advertising_classification.category", {defaultValue: "Typ"})} renderNumber={renderNumber} renderCurrencyValue={renderCurrencyValue} renderPercentValue={renderPercentValue} t={t}/>
                <Panel loading={loading} title={t("reporting_lab.advertising_classification.budget_flow", {defaultValue: "Tok rozpočtu: reklamní systém → formát → doména"})}><EChart option={strategyFlowOption(data?.categoryPlatforms ?? [], data?.categoryDomains ?? [], t, currency)} className="h-[520px]" loading={loading}/></Panel>
                <DomainTablePanel loading={loading} rows={domains} renderNumber={renderNumber} renderCurrencyValue={renderCurrencyValue} renderPercentValue={renderPercentValue} t={t}/>
            </>}

            {view === AdvertisingReportView.CampaignMix && <>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.campaigns_in_period", {defaultValue: "Kampaně v období"})} value={renderNumber(data?.campaignStrategyTotalCount ?? campaignCount)} helper={t("reporting_lab.advertising_classification.campaigns_in_period_help", {defaultValue: "Kampaně s ad-level aktivitou ve výběru"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.mixed_campaigns", {defaultValue: "Smíšené kampaně"})} value={renderNumber(mixedCampaigns)} helper={t("reporting_lab.advertising_classification.mixed_campaigns_help", {defaultValue: "Obsahují více než jeden rozpoznaný typ reklamy"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.mixed_campaign_share", {defaultValue: "Podíl smíšených kampaní"})} value={renderPercentValue(percentage(mixedCampaigns, campaignCount))} helper={t("reporting_lab.advertising_classification.mixed_campaign_share_help", {defaultValue: "Podíl z kampaní s ad-level aktivitou"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.used_formats", {defaultValue: "Použité formáty"})} value={renderNumber(usedCategoryCount)} helper={t("reporting_lab.advertising_classification.used_formats_help", {defaultValue: "Rozpoznané formáty s výkonem ve výběru"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.ads_per_campaign", {defaultValue: "Reklam na kampaň"})} value={campaignCount > 0 ? formatChartNumber(campaignAdCount / campaignCount, {maximumFractionDigits: 1}) : "–"} helper={t("reporting_lab.advertising_classification.ads_per_campaign_help", {defaultValue: "Průměr aktivních reklam v kampaních ve výběru"})}/>
                    <KpiCard loading={loading} label={t("reporting_lab.advertising_classification.highest_spend_campaign", {defaultValue: "Největší kampaň"})} value={dominantCampaign?.name ?? "–"} valueTooltip={dominantCampaign?.name ?? null} helper={dominantCampaign ? t("reporting_lab.advertising_classification.dominant_share", {defaultValue: "{{share}} rozpočtu", share: renderPercentValue(dominantCampaign.spendShare)}) : ""}/>
                </div>
                <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                    <Panel loading={loading} title={t("reporting_lab.advertising_classification.campaign_format_comparison", {defaultValue: "Formáty v kampaních s nejvyšším spendem"})}><EChart option={campaignCategorySpendOption(data?.campaignCategories ?? [], t, currency)} className="h-[500px]" loading={loading}/></Panel>
                    <Panel loading={loading} title={t("reporting_lab.advertising_classification.campaign_efficiency", {defaultValue: "Efektivita kampaní: CTR × CPC"})}><EChart option={campaignEfficiencyOption(campaignStrategies, t, currency)} className="h-[500px]" loading={loading}/></Panel>
                </div>
                <CampaignStrategyTable loading={loading} rows={campaignStrategies} totalCount={data?.campaignStrategyTotalCount ?? campaignStrategies.length} truncated={data?.campaignStrategiesTruncated ?? false} renderNumber={renderNumber} renderCurrencyValue={renderCurrencyValue} renderPercentValue={renderPercentValue} t={t}/>
            </>}
        </div>
    </WideSecureLayout>;
}

function DomainTablePanel({loading, rows, renderNumber, renderCurrencyValue, renderPercentValue, t}) {
    const normalizedRows = useMemo(() => rows.map((row) => ({...row, type: row.domain ?? "unassigned"})), [rows]);

    return <SummaryTablePanel
        loading={loading}
        title={t("reporting_lab.advertising_classification.by_domain", {defaultValue: "Výkon podle domény"})}
        rows={normalizedRows}
        labelFor={(value) => domainLabel(t, value)}
        firstLabel={t("reporting_lab.advertising_classification.domain", {defaultValue: "Doména"})}
        renderNumber={renderNumber}
        renderCurrencyValue={renderCurrencyValue}
        renderPercentValue={renderPercentValue}
        t={t}
    />;
}

export function CampaignStrategyTable({loading, rows, totalCount, truncated, renderNumber, renderCurrencyValue, renderPercentValue, t}) {
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const normalizedRows = useMemo(() => rows.map((row) => ({
        ...row,
        spendAmount: moneyAmount(row.spend),
        cpcAmount: moneyAmount(row.cpc),
        cpmAmount: moneyAmount(row.cpm),
        categorySearch: (row.categories ?? []).map((category) => categoryLabel(t, category)).join(" "),
        domainSearch: (row.domains ?? []).map((domain) => domainLabel(t, domain)).join(" "),
    })), [rows, t]);
    const filteredRows = useMemo(() => filterRowsBySearch(normalizedRows, search, (row) => [
        row.name,
        platformLabel(row.platform),
        row.categorySearch,
        row.domainSearch,
    ]), [normalizedRows, search]);
    const {sortedRows, sort, toggleSort} = useAdvertisingClassificationSortableRows(filteredRows);
    const pageCount = Math.max(1, Math.ceil(sortedRows.length / pageSize));
    const safePage = Math.min(page, pageCount);
    const visibleRows = sortedRows.slice((safePage - 1) * pageSize, safePage * pageSize);

    useEffect(() => setPage(1), [search]);

    return <Panel loading={loading} title={t("reporting_lab.advertising_classification.campaign_strategy_table", {defaultValue: "Kampaně a jejich reklamní formáty"})}>
        <TableSearch value={search} onChange={setSearch} visible={rows.length > 15 || search.length > 0} t={t}/>
        <div className="overflow-x-auto">
            <table className="min-w-[1320px] divide-y divide-slate-100 text-sm dark:divide-slate-700">
                <thead><tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                    <SortableHeader label={t("reporting_lab.advertising_classification.campaign", {defaultValue: "Kampaň"})} sortKey="name" sort={sort} onSort={toggleSort}/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.platform", {defaultValue: "Reklamní systém"})} sortKey="platform" sort={sort} onSort={toggleSort}/>
                    <th>{t("reporting_lab.advertising_classification.formats", {defaultValue: "Formáty reklam"})}</th>
                    <th>{t("reporting_lab.advertising_classification.target_domains", {defaultValue: "Cílové domény"})}</th>
                    <SortableHeader label={t("reporting_lab.advertising_classification.ads", {defaultValue: "Reklamy"})} sortKey="adCount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.spend", {defaultValue: "Spend"})} sortKey="spendAmount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.share", {defaultValue: "Podíl"})} sortKey="spendShare" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CTR" sortKey="ctr" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CPC" sortKey="cpcAmount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CPM" sortKey="cpmAmount" sort={sort} onSort={toggleSort} align="right"/>
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">{visibleRows.map((row) => <tr key={row.campaignId}>
                    <td className="max-w-[280px] py-3 pr-4">
                        <div className="truncate font-semibold text-slate-900 dark:text-slate-100" title={row.name}>{row.name}</div>
                        <div className="mt-1 text-xs text-slate-400">{row.composition === "mixed" ? categoryLabel(t, "mixed") : categoryLabel(t, row.composition)}</div>
                    </td>
                    <td className="whitespace-nowrap py-3 pr-4 text-slate-600 dark:text-slate-300">{platformLabel(row.platform)}</td>
                    <td className="max-w-[330px] py-3 pr-4"><div className="flex flex-wrap gap-1.5">{(row.categories ?? []).map((category) => <span key={category} className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200"><span className="h-2 w-2 rounded-full" style={{backgroundColor: CategoryColors[category] ?? "#64748b"}}/>{categoryLabel(t, category)}</span>)}</div></td>
                    <td className="max-w-[260px] py-3 pr-4 text-slate-600 dark:text-slate-300">{(row.domains ?? []).map((domain) => domainLabel(t, domain)).join(", ")}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.adCount)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.spend, true)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.spendShare)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.ctr)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.cpc, true)}</td>
                    <td className="whitespace-nowrap py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.cpm, true)}</td>
                </tr>)}</tbody>
            </table>
        </div>
        {truncated && <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">{t("reporting_lab.advertising_classification.campaign_strategy_limited", {defaultValue: "Tabulka zobrazuje {{shown}} kampaní s nejvyšším spendem z celkových {{total}} kampaní ve výběru.", shown: renderNumber(rows.length), total: renderNumber(totalCount)})}</p>}
        <TablePagination page={safePage} pageSize={pageSize} totalItems={sortedRows.length} onPageChange={setPage} onPageSizeChange={(value) => { setPageSize(value); setPage(1); }} t={t}/>
    </Panel>;
}

export function useAdvertisingClassificationSortableRows(rows, initialKey = "spendAmount") {
    return useSortableRows(rows, initialKey, (row, key) => row[key], "desc");
}

function SummaryTablePanel({loading, title, rows, labelFor, firstLabel, renderNumber, renderCurrencyValue, renderPercentValue, t}) {
    const sortableRows = useMemo(() => rows.map((row) => ({
        ...row,
        spendAmount: moneyAmount(row.spend),
        cpcAmount: moneyAmount(row.cpc),
        cpmAmount: moneyAmount(row.cpm),
    })), [rows]);
    const {sortedRows, sort, toggleSort} = useAdvertisingClassificationSortableRows(sortableRows);

    return <Panel loading={loading} title={title}>
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                <thead><tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                    <SortableHeader label={firstLabel} sortKey="type" sort={sort} onSort={toggleSort}/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.campaigns", {defaultValue: "Kampaně"})} sortKey="campaignCount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.ads", {defaultValue: "Reklamy"})} sortKey="adCount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.spend", {defaultValue: "Spend"})} sortKey="spendAmount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label={t("reporting_lab.advertising_classification.share", {defaultValue: "Podíl"})} sortKey="spendShare" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CTR" sortKey="ctr" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CPC" sortKey="cpcAmount" sort={sort} onSort={toggleSort} align="right"/>
                    <SortableHeader label="CPM" sortKey="cpmAmount" sort={sort} onSort={toggleSort} align="right"/>
                </tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">{sortedRows.map((row) => <tr key={row.type}>
                    <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{labelFor(row.type)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.campaignCount)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.adCount)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.spend, true)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.spendShare)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderPercentValue(row.ctr)}</td>
                    <td className="whitespace-nowrap py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.cpc, true)}</td>
                    <td className="whitespace-nowrap py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(row.cpm, true)}</td>
                </tr>)}</tbody>
            </table>
        </div>
    </Panel>;
}
